import { Flag } from '@/components/ui/flag/flag';
import type { ResolvedTimeline } from '../types';
import { CheckeredFlagIcon } from './CheckeredFlagIcon';
import { RideCascadePanel } from './RideCascadePanel';

export function HorizontalTimeline({
  rideDayTimeline,
  expandedCountry,
  setExpandedCountry,
  horizontalZoom,
  setHorizontalZoom,
  nowPositionPct,
  effectiveNowMs,
}: {
  rideDayTimeline: ResolvedTimeline;
  expandedCountry: string | null;
  setExpandedCountry: (fn: (prev: string | null) => string | null) => void;
  horizontalZoom: number;
  setHorizontalZoom: (fn: (z: number) => number) => void;
  nowPositionPct: number | null;
  effectiveNowMs: number;
}) {
  // Inset everything from the edges so the first/last flag and the
  // 00:00/22:00 UTC labels aren't clipped by the rounded container.
  const BUFFER_PCT = 5;
  const remap = (p: number) => BUFFER_PCT + (p * (100 - 2 * BUFFER_PCT)) / 100;
  const remappedNowPct = nowPositionPct === null ? null : remap(nowPositionPct);
  return (
    <div className="relative rounded-2xl border border-stone-950/10 bg-white/70 px-4 pb-12 pt-14 shadow-sm shadow-stone-950/5 sm:px-6 md:px-10">
      {/* Zoom controls */}
      <div className="absolute right-3 top-3 z-30 inline-flex items-center gap-2 rounded-full border border-stone-950/10 bg-white/85 px-1 py-0.5">
        <button
          type="button"
          aria-label="Zoom out"
          onClick={() => setHorizontalZoom((z) => Math.max(1, z / 1.5))}
          disabled={horizontalZoom <= 1}
          className="flex h-6 w-6 items-center justify-center rounded-full text-stone-700 transition hover:bg-stone-950/10 disabled:cursor-not-allowed disabled:text-stone-400 disabled:hover:bg-transparent"
        >
          <svg viewBox="0 0 12 12" className="h-3 w-3" fill="none" aria-hidden="true">
            <path d="M2 6 H10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
        <span className="min-w-[2rem] text-center font-mono text-[10px] tabular-nums text-stone-600">
          {horizontalZoom.toFixed(1).replace(/\.0$/, '')}×
        </span>
        <button
          type="button"
          aria-label="Zoom in"
          onClick={() => setHorizontalZoom((z) => Math.min(5, z * 1.5))}
          disabled={horizontalZoom >= 5}
          className="flex h-6 w-6 items-center justify-center rounded-full text-stone-700 transition hover:bg-stone-950/10 disabled:cursor-not-allowed disabled:text-stone-400 disabled:hover:bg-transparent"
        >
          <svg viewBox="0 0 12 12" className="h-3 w-3" fill="none" aria-hidden="true">
            <path
              d="M6 2 V10 M2 6 H10"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      <div className="-mx-4 overflow-x-auto px-4 pb-2 md:mx-0 md:px-0">
        <div className="min-w-[820px] md:min-w-0" style={{ width: `${horizontalZoom * 100}%` }}>
          {/* UTC hour labels (top) */}
          <div className="relative mb-6 h-6">
            {[0, 25, 50, 75, 100].map((pct, idx, arr) => {
              if (
                rideDayTimeline.timelineMarkerStart === null ||
                rideDayTimeline.timelineMarkerEnd === null
              ) {
                return null;
              }
              const ts =
                rideDayTimeline.timelineMarkerStart +
                ((rideDayTimeline.timelineMarkerEnd - rideDayTimeline.timelineMarkerStart) * pct) /
                  100;
              const d = new Date(ts);
              const label = `${String(d.getUTCHours()).padStart(2, '0')}:00`;
              return (
                <div
                  key={pct}
                  className="absolute -translate-x-1/2 text-[10px] uppercase tracking-wider text-stone-600"
                  style={{
                    left: `${remap(pct)}%`,
                    textAlign: idx === 0 ? 'left' : idx === arr.length - 1 ? 'right' : 'center',
                  }}
                >
                  {label} UTC
                </div>
              );
            })}
          </div>

          {/* Track */}
          <div className="relative h-72">
            <div
              className="absolute top-1/2 h-[2px] -translate-y-1/2 rounded-full bg-stone-950/15"
              style={{ left: `${BUFFER_PCT}%`, right: `${BUFFER_PCT}%` }}
            />
            {remappedNowPct !== null && (
              <div
                className="absolute top-1/2 h-[2px] -translate-y-1/2 rounded-full bg-gradient-to-r from-dgd-gold/40 via-dgd-gold to-dgd-gold/70"
                style={{ left: `${BUFFER_PCT}%`, width: `${remappedNowPct - BUFFER_PCT}%` }}
              />
            )}
            {rideDayTimeline.entries.map((entry, entryIdx) => {
              if (
                rideDayTimeline.timelineMarkerStart === null ||
                rideDayTimeline.timelineMarkerEnd === null
              ) {
                return null;
              }
              const isFirst = entryIdx === 0;
              // 4-lane vertical stagger: alternate flags between two heights
              // on each side of the centerline so adjacent markers don't pile
              // up. Order: far-above, near-below, near-above, far-below.
              const laneIdx = entryIdx % 4;
              const above = laneIdx === 0 || laneIdx === 2;
              const far = laneIdx === 0 || laneIdx === 3;
              const offsetPx = far ? 92 : 44;
              const span = rideDayTimeline.timelineMarkerEnd - rideDayTimeline.timelineMarkerStart;
              const pct =
                span > 0
                  ? ((entry.earliestStartUtcMs - rideDayTimeline.timelineMarkerStart) / span) * 100
                  : 50;
              const utcLabel = `${entry.rides[0]?.utcLabel ?? '--:--'} UTC · ${
                entry.rides[0]?.localLabel ?? '--:--'
              } local`;
              const isExpanded = expandedCountry === entry.countryCode;
              const status = rideDayTimeline.statusByCountryCode[entry.countryCode];
              const entryStatus = status?.status ?? 'upcoming';
              return (
                <button
                  type="button"
                  key={entry.countryCode}
                  onClick={() =>
                    setExpandedCountry((prev) =>
                      prev === entry.countryCode ? null : entry.countryCode,
                    )
                  }
                  className={`group/marker absolute -translate-x-1/2 -translate-y-1/2 cursor-pointer rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-dgd-gold focus-visible:ring-offset-2 focus-visible:ring-offset-dgd-gold-tint ${
                    isExpanded ? 'z-30' : ''
                  }`}
                  style={{
                    left: `${remap(Math.max(0, Math.min(100, pct)))}%`,
                    top: above ? `calc(50% - ${offsetPx}px)` : `calc(50% + ${offsetPx}px)`,
                  }}
                >
                  <div
                    className="absolute left-1/2 w-px -translate-x-1/2 bg-stone-950/20"
                    style={{
                      height: `${offsetPx - 20}px`,
                      ...(above ? { top: '100%' } : { bottom: '100%' }),
                    }}
                  />
                  {isFirst && (
                    <div
                      className={`absolute left-1/2 -translate-x-1/2 ${
                        above ? 'bottom-[calc(100%+1.5rem)]' : 'top-[calc(100%+1.5rem)]'
                      } whitespace-nowrap rounded-full bg-dgd-gold/20 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.15em] text-dgd-gold-hover`}
                    >
                      Day starts
                    </div>
                  )}
                  <div
                    className={`relative transition-transform duration-300 group-hover/marker:scale-110 ${
                      isExpanded ? 'scale-110' : ''
                    } ${isFirst ? 'h-12 w-12' : 'h-11 w-11'}`}
                  >
                    {entryStatus === 'live' && (
                      <div className="pointer-events-none absolute inset-0 animate-pulse rounded-full ring-2 ring-dgd-gold ring-offset-2 ring-offset-dgd-gold-tint" />
                    )}
                    {isExpanded && entryStatus !== 'live' && (
                      <div className="pointer-events-none absolute inset-0 rounded-full ring-2 ring-dgd-gold ring-offset-2 ring-offset-dgd-gold-tint" />
                    )}
                    <div className="relative h-full w-full overflow-hidden rounded-full shadow-md shadow-stone-950/15">
                      <Flag
                        code={entry.countryCode}
                        alt={entry.countryName}
                        rounded="none"
                        status={entryStatus === 'completed' ? 'completed' : 'none'}
                        className="h-full w-full object-cover"
                      />
                      <div
                        className={`pointer-events-none absolute inset-0 rounded-full ring-2 ring-inset ${
                          entryStatus === 'live'
                            ? 'ring-dgd-gold'
                            : entryStatus === 'completed'
                              ? 'ring-stone-400'
                              : 'ring-stone-950/30'
                        }`}
                      />
                      {entryStatus === 'completed' && (
                        <div className="absolute inset-0 flex items-center justify-center bg-stone-950/60">
                          <CheckeredFlagIcon className="h-4 w-4 text-stone-100" />
                        </div>
                      )}
                    </div>
                  </div>
                  <div
                    className={`absolute left-1/2 flex -translate-x-1/2 flex-col items-center ${
                      above ? 'bottom-[calc(100%+0.5rem)]' : 'top-[calc(100%+0.5rem)]'
                    }`}
                  >
                    <span className="whitespace-nowrap text-[9px] text-stone-600">
                      {entry.rideCount} {entry.rideCount === 1 ? 'drive' : 'drives'}
                    </span>
                  </div>
                  <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-3 w-44 -translate-x-1/2 rounded-lg border border-stone-950/10 bg-white/95 px-3 py-2 text-center opacity-0 shadow-xl backdrop-blur transition-opacity duration-200 group-hover/marker:opacity-100">
                    <p className="text-xs font-semibold text-stone-950">{entry.countryName}</p>
                    <p className="mt-0.5 text-[10px] text-stone-600">
                      {entry.rideCount} {entry.rideCount === 1 ? 'drive' : 'drives'} · {utcLabel}
                    </p>
                  </div>
                </button>
              );
            })}
            {remappedNowPct !== null && (
              <div
                className="pointer-events-none absolute top-0 z-20 flex h-full -translate-x-1/2 flex-col items-center"
                style={{ left: `${remappedNowPct}%` }}
              >
                <div className="rounded-full bg-dgd-gold px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white shadow-lg shadow-dgd-gold/30">
                  Now
                </div>
                <div className="mt-0.5 h-full w-px bg-dgd-gold/70" />
              </div>
            )}
            <div
              className="absolute flex -translate-x-1/2 flex-col items-center"
              style={{ left: `${remap(100)}%`, top: 'calc(50% + 5.75rem)' }}
            >
              <div className="h-9 w-px bg-stone-950/20" />
              <div className="mt-2 whitespace-nowrap rounded-full bg-dgd-gold/20 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.15em] text-dgd-gold-hover">
                Day ends
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cascade panel */}
      {expandedCountry &&
        (() => {
          const entry = rideDayTimeline.entries.find((e) => e.countryCode === expandedCountry);
          if (!entry) return null;
          return (
            <RideCascadePanel
              entry={entry}
              effectiveNowMs={effectiveNowMs}
              onClose={() => setExpandedCountry(() => null)}
            />
          );
        })()}
    </div>
  );
}
